import {
  Alert,
  Anchor,
  Badge,
  Button,
  Code,
  CopyButton,
  FileInput,
  Group,
  List,
  Modal,
  NumberInput,
  Pagination,
  PasswordInput,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconCopy, IconPhoto, IconPlugConnected, IconSend } from '@tabler/icons-react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, izvedi as izvediKlic, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { denar, stevilo, tocke, trajanje } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { KLJUC_AI } from './ai';
import { CenikAi, FunkcijeAi } from './FunkcijeAi';
import { cas } from './skrbnistvo';
import { glave, obNapaki } from './zeton';

type Povezava = Shema['AiPovezava'];
type Preverjanje = Shema['AiPreverjanje'];

const VELIKOST = 25;
/** Da brskalnik ne pošilja prevelikih datotek; število in vrste preveri strežnik. */
const NAJVEC_SLIKE = 15 * 1024 * 1024;

/** Imena ponudnikov za prikaz (strežnik vrne podprte v {@code ponudniki}). */
const PONUDNIKI: Record<string, string> = { GEMINI: 'Google Gemini' };

/** Klici iz skrbništva za filter evidence (funkcije za stranke da strežnik). */
const FUNKCIJE_SKRBNISTVA: Record<string, string> = {
  PREIZKUS: 'Preizkus',
  PREVERJANJE: 'Preverjanje modela',
};

/** Skupno za klice skrbništva: napaka v obliki za {@code useQuery} (zaklep seje pri 403). */
async function klic<T>(k: () => Promise<T>): Promise<T> {
  try {
    return await k();
  } catch (e) {
    obNapaki(e);
    throw e;
  }
}

/** Modeli, ki jih ponuja shranjeni ključ (preverjanje pri ponudniku); osveži se po shranjevanju. */
function useModeli(imaKljuc: boolean) {
  return useQuery({
    queryKey: [...KLJUC_AI, 'modeli'],
    enabled: imaKljuc,
    staleTime: 5 * 60_000,
    queryFn: () => klic(() => podatki(api.POST('/api/skrbnik/ai/preveri', { headers: glave() }))),
  });
}

/**
 * Skrbništvo → Umetna inteligenca: povezava z jezikovnim modelom (za zdaj Google Gemini; ključ šifriran na strežniku,
 * nazaj se pokaže samo začetek in konec), izbira modela, privzeta mesečna omejitev instanc, preizkus (tudi s sliko),
 * poraba instanc v tekočem mesecu in evidenca klicev. Vklop za posamezno instanco je pri podrobnostih instance.
 */
export function UmetnaInteligencaSkrbnistvo() {
  const povezava = useQuery({
    queryKey: KLJUC_AI,
    queryFn: () => klic(() => podatki(api.GET('/api/skrbnik/ai', { headers: glave() }))),
  });
  const modeli = useModeli(!!povezava.data?.apiKljuc);

  if (!povezava.data) {
    return (
      <Kartica>
        <StanjeNalaganja
          napaka={povezava.error}
          ponovi={() => void povezava.refetch()}
          naslov="Nastavitev umetne inteligence ni bilo mogoče naložiti"
        />
      </Kartica>
    );
  }
  const p = povezava.data;
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <NastavitevPovezave
          key={`${p.spremenjeno ?? 0}`}
          p={p}
          modeli={modeli.data}
          nalaganjeModelov={modeli.isFetching}
        />
        <Navodila />
      </SimpleGrid>
      <CenikAi izbrani={p.model} modeli={modeli.data} />
      <FunkcijeAi modeli={modeli.data} />
      {p.apiKljuc && <Preizkus p={p} modeli={modeli.data} />}
      <Poraba />
      <Klici />
    </Stack>
  );
}

function NastavitevPovezave({
  p,
  modeli,
  nalaganjeModelov,
}: {
  p: Povezava;
  modeli?: Preverjanje;
  nalaganjeModelov: boolean;
}) {
  const odjemalec = useQueryClient();
  const [ponudnik, setPonudnik] = useState(p.ponudnik ?? 'GEMINI');
  const [apiKljuc, setApiKljuc] = useState('');
  const [model, setModel] = useState<string | null>(p.model ?? null);
  const [omejitev, setOmejitev] = useState<number | string>(p.omejitevTock ?? '');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<'shrani' | 'preveri' | 'odklopi' | null>(null);
  const [preverjanje, setPreverjanje] = useState<Preverjanje | null>(null);
  const [odklop, setOdklop] = useState(false);

  const izvedi = async (kaj: 'shrani' | 'preveri' | 'odklopi', k: () => Promise<void>) => {
    setDela(kaj);
    setNapake({});
    setNapaka(null);
    try {
      await k();
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(null);
    }
  };

  const shrani = () =>
    izvedi('shrani', async () => {
      const nova = await podatki(
        api.PUT('/api/skrbnik/ai', {
          headers: glave(),
          body: {
            ponudnik,
            apiKljuc: apiKljuc.trim() || undefined,
            model: model ?? undefined,
            omejitevTock: typeof omejitev === 'number' ? omejitev : undefined,
          },
        }),
      );
      setApiKljuc('');
      setPreverjanje(null);
      odjemalec.setQueryData(KLJUC_AI, nova);
      await odjemalec.invalidateQueries({ queryKey: [...KLJUC_AI, 'modeli'] });
      await odjemalec.invalidateQueries({ queryKey: [...KLJUC_AI, 'poraba'] });
    });

  const preveri = () =>
    izvedi('preveri', async () => {
      const r = await podatki(api.POST('/api/skrbnik/ai/preveri', { headers: glave() }));
      setPreverjanje(r);
      odjemalec.setQueryData([...KLJUC_AI, 'modeli'], r);
    });

  const odklopi = () =>
    izvedi('odklopi', async () => {
      await izvediKlic(api.DELETE('/api/skrbnik/ai', { headers: glave() }));
      setOdklop(false);
      setPreverjanje(null);
      setModel(null);
      await odjemalec.invalidateQueries({ queryKey: KLJUC_AI });
    });

  const ponujeni = modeli?.modeli ?? [];
  const moznosti = ponujeni.map((m) => ({ value: m.id ?? '', label: `${m.naziv ?? m.id} (${m.id})` }));
  if (model && !moznosti.some((m) => m.value === model)) {
    moznosti.unshift({ value: model, label: model });
  }
  const izbrani = ponujeni.find((m) => m.id === model);
  const drugPonudnik = !!p.apiKljuc && ponudnik !== p.ponudnik;

  return (
    <Kartica naslov="Povezava" dejanja={<StanjePovezave p={p} />}>
      <Stack gap="sm">
        <Podatki
          vrstice={[
            ['Ponudnik', PONUDNIKI[p.ponudnik ?? ''] ?? p.ponudnik],
            ['Ključ (API)', p.apiKljuc ? <Code key="k">{p.apiKljuc}</Code> : 'ni vpisan'],
            ['Model', p.model ? <Code key="m">{p.model}</Code> : 'ni izbran'],
            [
              'Privzeta mesečna omejitev',
              p.omejitevTock ? `${denar(p.omejitevTock)} točk na instanco` : 'brez omejitve',
            ],
            ['Nazadnje spremenil', p.spremenil ? `${p.spremenil}, ${cas(p.spremenjeno)}` : ''],
          ]}
        />
        {preverjanje && <RezultatPreverjanja r={preverjanje} />}
        <Select
          label="Ponudnik"
          data={(p.ponudniki ?? ['GEMINI']).map((v) => ({ value: v, label: PONUDNIKI[v] ?? v }))}
          value={ponudnik}
          onChange={(v) => v && setPonudnik(v)}
          allowDeselect={false}
          error={napake.ponudnik}
        />
        <PasswordInput
          label="Ključ (API)"
          description={
            p.apiKljuc && !drugPonudnik
              ? 'Vpišite samo, če ga želite zamenjati.'
              : 'Google AI Studio → Get API key (začne se z AIza…)'
          }
          placeholder={p.apiKljuc && !drugPonudnik ? p.apiKljuc : 'AIza…'}
          value={apiKljuc}
          onChange={(e) => setApiKljuc(e.currentTarget.value)}
          error={napake.apiKljuc}
          autoComplete="off"
        />
        <Select
          label="Model"
          description={
            izbrani?.opis ??
            (p.apiKljuc
              ? 'Modeli, ki jih ponuja shranjeni ključ. Za začetek hiter in poceni model (Flash ali Flash-Lite).'
              : 'Najprej shranite ključ, nato izberite model.')
          }
          placeholder={nalaganjeModelov ? 'Nalagam modele …' : 'Izberite model'}
          data={moznosti}
          value={model}
          onChange={setModel}
          searchable
          clearable
          nothingFoundMessage="Ni takega modela"
          disabled={!p.apiKljuc}
          error={napake.model}
        />
        <NumberInput
          size="md"
          label="Privzeta mesečna omejitev na instanco (točke)"
          description="Največ toliko točk (z maržo in DDV) lahko instanca porabi za umetno inteligenco v koledarskem mesecu. Prazno = brez omejitve; instanca ima lahko svojo."
          placeholder="Brez omejitve"
          value={omejitev}
          onChange={setOmejitev}
          min={0.01}
          decimalScale={2}
          thousandSeparator="."
          decimalSeparator=","
          hideControls
          error={napake.omejitevTock}
        />
        {napaka && (
          <Alert color="red" variant="light" role="alert">
            {napaka}
          </Alert>
        )}
        <Group gap="xs">
          <Button
            onClick={() => void shrani()}
            loading={dela === 'shrani'}
            disabled={dela !== null && dela !== 'shrani'}
          >
            Shrani
          </Button>
          <Button
            variant="default"
            leftSection={<IconPlugConnected size={18} />}
            onClick={() => void preveri()}
            loading={dela === 'preveri'}
            disabled={!p.apiKljuc || (dela !== null && dela !== 'preveri')}
          >
            Preveri povezavo
          </Button>
          {p.apiKljuc && (
            <Button variant="subtle" color="red" onClick={() => setOdklop(true)} disabled={dela !== null}>
              Odklopi
            </Button>
          )}
        </Group>
      </Stack>
      <Modal opened={odklop} onClose={() => setOdklop(false)} title="Odklopi umetno inteligenco" radius="lg">
        <Stack gap="sm">
          <Text size="sm">
            Ključ in model se izbrišeta. Dokler ju znova ne vpišete, umetna inteligenca v nobeni instanci ne deluje;
            vklop pri instancah in evidenca klicev ostaneta.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => setOdklop(false)}>
              Prekliči
            </Button>
            <Button color="red" onClick={() => void odklopi()} loading={dela === 'odklopi'}>
              Odklopi
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Kartica>
  );
}

function StanjePovezave({ p }: { p: Povezava }) {
  return p.povezano ? (
    <Badge color="green" variant="light">
      povezano
    </Badge>
  ) : (
    <Badge color="gray" variant="light">
      {p.apiKljuc ? 'model ni izbran' : 'ni povezano'}
    </Badge>
  );
}

function RezultatPreverjanja({ r }: { r: Preverjanje }) {
  if (!r.uspelo) {
    return (
      <Alert
        color={r.modeli?.length ? 'yellow' : 'red'}
        variant="light"
        role="alert"
        title="Povezava ne deluje v celoti"
      >
        {r.sporocilo}
      </Alert>
    );
  }
  return (
    <Alert color="green" variant="light" icon={<IconCheck size={18} />} title="Povezava deluje">
      <Text size="sm">
        {r.sporocilo ?? 'Ključ deluje, izbrani model je na voljo.'} Ključ ponuja {stevilo(r.modeli?.length ?? 0)}{' '}
        modelov za besedilo.
      </Text>
    </Alert>
  );
}

function Navodila() {
  return (
    <Kartica naslov="Nastavitev pri Googlu">
      <Stack gap="sm">
        <Text size="sm">
          Vsako okolje ima svojo povezavo (razvojni strežnik svoj ključ, produkcija svojega), da se poraba ne meša.
        </Text>
        <List type="ordered" spacing="xs" size="sm">
          <List.Item>
            V{' '}
            <Anchor href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">
              Google AI Studio
            </Anchor>{' '}
            izberite <b>Get API key → Create API key</b> v projektu Google Cloud podjetja. Ključ prilepite v kartico
            Povezava in <b>Shrani</b> – strežnik ga preizkusi pri Googlu.
          </List.Item>
          <List.Item>
            Za projekt vklopite <b>plačljivo uporabo (Billing)</b>. Pri brezplačni ravni sme Google vsebino klicev
            uporabiti za izboljšave svojih storitev – tu gre za podatke strank, zato samo plačljiva raven.
          </List.Item>
          <List.Item>
            Izberite <b>model</b> in po želji privzeto mesečno omejitev, nato <b>Shrani</b>. Kakovost (npr. branje
            prometnega dovoljenja s fotografije) preverite spodaj pri <b>Preizkusu</b>; z drugim modelom lahko
            primerjate, ne da bi ga shranili.
          </List.Item>
          <List.Item>
            V <b>Cenik modelov</b> vpišite ceno izbranega modela – brez nje stranke umetne inteligence ne morejo
            uporabljati. Strankam je nato na voljo samodejno (plačajo jo s točkami, potrebna je vsaj 1 točka); pri
            instanci jo lahko prepoveste ali omejite.
          </List.Item>
        </List>
        <Alert color="yellow" variant="light">
          Preden jo vklopite strankam: v pravilih zasebnosti mora biti Google naveden kot obdelovalec podatkov. Evidenca
          klicev hrani kdo, za kaj, porabo in trajanje, 90 dni pa tudi zahtevo in odgovor (brez slik in dokumentov).
        </Alert>
      </Stack>
    </Kartica>
  );
}

/** Preizkus modela z vprašanjem in neobvezno sliko (ne šteje v omejitve instanc, je pa v evidenci klicev). */
function Preizkus({ p, modeli }: { p: Povezava; modeli?: Preverjanje }) {
  const odjemalec = useQueryClient();
  const [navodila, setNavodila] = useState('');
  const [vprasanje, setVprasanje] = useState('');
  const [slike, setSlike] = useState<File[]>([]);
  const [model, setModel] = useState<string | null>(null);
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const [odgovor, setOdgovor] = useState<Shema['AiPreizkusOdgovor'] | null>(null);

  const poslji = async () => {
    setNapake({});
    setNapaka(null);
    if (slike.reduce((v, f) => v + f.size, 0) > NAJVEC_SLIKE) {
      setNapake({ slike: 'Slike so skupaj prevelike (največ 15 MB).' });
      return;
    }
    setDela(true);
    try {
      const o = await podatki(
        api.POST('/api/skrbnik/ai/preizkus', {
          headers: glave(),
          body: {
            navodila: navodila.trim() || undefined,
            vprasanje,
            slike: slike.length
              ? await Promise.all(slike.map(async (f) => ({ podatki: await base64(f), vrsta: f.type })))
              : undefined,
            model: model ?? undefined,
          },
        }),
      );
      setOdgovor(o);
    } catch (e) {
      obNapaki(e);
      setOdgovor(null);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
      await odjemalec.invalidateQueries({ queryKey: [...KLJUC_AI, 'klici'] });
    }
  };

  return (
    <Kartica naslov="Preizkus">
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Stack gap="sm">
          <Textarea
            size="md"
            label="Navodila (neobvezno)"
            description="Sistemska navodila, npr. »Iz fotografije prometnega dovoljenja preberi VIN, znamko, datum prve registracije in moč (kW). Odgovori v JSON.«"
            value={navodila}
            onChange={(e) => setNavodila(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={8}
          />
          <Textarea
            size="md"
            label="Vprašanje"
            value={vprasanje}
            onChange={(e) => setVprasanje(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={8}
            error={napake.vprasanje}
          />
          <FileInput
            size="md"
            multiple
            label="Slike (neobvezno)"
            description="Do 5 slik naenkrat (npr. obe strani prometnega dovoljenja), JPEG, PNG, WebP ali HEIC, skupaj največ 15 MB"
            placeholder="Izberite slike"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            leftSection={<IconPhoto size={16} />}
            value={slike}
            onChange={setSlike}
            clearable
            error={napake.slike}
          />
          <Select
            label="Model"
            description="Prazno = shranjeni model. Drug model za primerjavo se ne shrani."
            placeholder={p.model ?? 'Shranjeni model'}
            data={(modeli?.modeli ?? []).map((m) => ({ value: m.id ?? '', label: `${m.naziv ?? m.id} (${m.id})` }))}
            value={model}
            onChange={setModel}
            searchable
            clearable
            error={napake.model}
          />
          {napaka && (
            <Alert color="red" variant="light" role="alert">
              {napaka}
            </Alert>
          )}
          <Group>
            <Button leftSection={<IconSend size={18} />} onClick={() => void poslji()} loading={dela}>
              Pošlji
            </Button>
          </Group>
        </Stack>
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Odgovor
          </Text>
          {odgovor ? (
            <>
              <Text size="xs" c="dimmed">
                {odgovor.model} · {stevilo(odgovor.vhodniZetoni ?? 0)} vhodnih + {stevilo(odgovor.izhodniZetoni ?? 0)}{' '}
                izhodnih žetonov · {trajanje(odgovor.trajanjeMs)}
              </Text>
              <Text
                size="sm"
                data-testid="odgovor-ai"
                style={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  fontFamily: 'var(--mantine-font-family-monospace)',
                }}
              >
                {odgovor.besedilo}
              </Text>
            </>
          ) : (
            <Text size="sm" c="dimmed">
              Tu se pokaže odgovor modela s porabo žetonov in trajanjem.
            </Text>
          )}
        </Stack>
      </SimpleGrid>
    </Kartica>
  );
}

/** Datoteka kot Base64 (brez predpone data:). */
function base64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error ?? new Error('Slike ni bilo mogoče prebrati.'));
    r.readAsDataURL(f);
  });
}

/** Poraba v točkah z mesečno omejitvijo (vrstica napredka, rdeča ob 90 %) in porabljeni žetoni. */
export function PorabaTock({
  zaracunano,
  omejitev,
  zetoni,
}: {
  zaracunano?: number;
  omejitev?: number;
  zetoni?: number;
}) {
  const p = zaracunano ?? 0;
  const zet = zetoni !== undefined ? ` · ${stevilo(zetoni)} žetonov` : '';
  if (!omejitev) {
    return (
      <Text size="sm">
        {tocke(p)} točk (brez omejitve){zet}
      </Text>
    );
  }
  const odstotek = Math.min(100, (p / omejitev) * 100);
  return (
    <Stack gap={4}>
      <Text size="sm">
        {tocke(p)} / {denar(omejitev)} točk{zet}
      </Text>
      <Progress value={odstotek} color={odstotek >= 90 ? 'red' : 'blue'} size="sm" aria-label="Poraba točk" />
    </Stack>
  );
}

function Poraba() {
  const poraba = useQuery({
    queryKey: [...KLJUC_AI, 'poraba'],
    queryFn: () => klic(() => podatki(api.GET('/api/skrbnik/ai/poraba', { headers: glave() }))),
  });
  return (
    <Kartica naslov="Poraba v tem mesecu">
      {!poraba.data ? (
        <StanjeNalaganja
          napaka={poraba.error}
          ponovi={() => void poraba.refetch()}
          naslov="Porabe ni bilo mogoče naložiti"
        />
      ) : poraba.data.length ? (
        <Table.ScrollContainer minWidth={640}>
          <Table verticalSpacing={8}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Instanca</Table.Th>
                <Table.Th>Stanje</Table.Th>
                <Table.Th>Poraba (zaračunano / omejitev)</Table.Th>
                <Table.Th ta="right">Klicev</Table.Th>
                <Table.Th ta="right">Napak</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {poraba.data.map((i) => (
                <Table.Tr key={i.companyId}>
                  <Table.Td>
                    <Anchor component={Link} to={`/skrbnistvo/${i.companyId}`} fw={600} size="sm">
                      {i.naziv}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    {i.prepovedano ? (
                      <Badge color="red" variant="light">
                        prepovedano
                      </Badge>
                    ) : i.izklopljeno ? (
                      <Badge color="gray" variant="light">
                        stranka izklopila
                      </Badge>
                    ) : (
                      <Badge color="green" variant="light">
                        na voljo
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td miw={200}>
                    <PorabaTock zaracunano={i.zaracunano} omejitev={i.veljavnaOmejitev} zetoni={i.porabljeno} />
                  </Table.Td>
                  <Table.Td ta="right">{stevilo(i.klicev ?? 0)}</Table.Td>
                  <Table.Td ta="right">{stevilo(i.napak ?? 0)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ) : (
        <Text size="sm" c="dimmed">
          Ta mesec umetne inteligence ni uporabila nobena instanca (na voljo je vsem, ki je nimajo prepovedane).
        </Text>
      )}
    </Kartica>
  );
}

/** Vrednost filtra instance za klice iz skrbništva (preizkus, preverjanje modela). */
const SKRBNISTVO = 'skrbnistvo';

/**
 * Evidenca klicev (najnovejši najprej, strani na strežniku) s filtri po instanci (tudi samo skrbništvo), funkciji in
 * stanju; klik na vrstico odpre zahtevo in odgovor ponudnika (brez slik).
 */
function Klici() {
  const [instanca, setInstanca] = useState<string | null>(null);
  const [funkcija, setFunkcija] = useState<string | null>(null);
  const [stanje, setStanje] = useState<string | null>(null);
  const [stran, setStran] = useState(0);
  const [izbran, setIzbran] = useState<number | null>(null);
  const instance = useQuery({
    queryKey: ['skrbnik', 'instance'],
    queryFn: ({ signal }) => klic(() => podatki(api.GET('/api/skrbnik/instance', { signal, headers: glave() }))),
  });
  const funkcije = useQuery({
    queryKey: [...KLJUC_AI, 'funkcije'],
    queryFn: () => klic(() => podatki(api.GET('/api/skrbnik/ai/funkcije', { headers: glave() }))),
  });
  const query = {
    companyId: instanca && instanca !== SKRBNISTVO ? Number(instanca) : undefined,
    skrbnistvo: instanca === SKRBNISTVO || undefined,
    funkcija: funkcija ?? undefined,
    uspelo: stanje ? stanje === 'uspelo' : undefined,
    stran,
    velikost: VELIKOST,
  };
  const klici = useQuery({
    queryKey: [...KLJUC_AI, 'klici', query],
    placeholderData: keepPreviousData,
    queryFn: () => klic(() => podatki(api.GET('/api/skrbnik/ai/klici', { headers: glave(), params: { query } }))),
  });
  const filter =
    <T,>(nastavi: (v: T) => void) =>
    (v: T) => {
      nastavi(v);
      setStran(0);
    };
  const filtrirano = !!(instanca || funkcija || stanje);
  const skupaj = klici.data?.skupaj ?? 0;
  const strani = Math.max(1, Math.ceil(skupaj / VELIKOST));
  return (
    <Kartica naslov="Evidenca klicev">
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          <Select
            label="Instanca"
            placeholder="Vse"
            searchable
            clearable
            nothingFoundMessage="Ni instance"
            data={[
              { value: SKRBNISTVO, label: 'Skrbništvo (preizkus, preverjanje)' },
              ...(instance.data ?? [])
                .filter((i) => i.companyId !== undefined)
                .map((i) => ({ value: String(i.companyId), label: `${i.naziv ?? i.domena} (#${i.companyId})` })),
            ]}
            value={instanca}
            onChange={filter(setInstanca)}
          />
          <Select
            label="Funkcija"
            placeholder="Vse"
            clearable
            data={[
              ...(funkcije.data ?? []).map((f) => ({ value: f.funkcija ?? '', label: f.naziv ?? f.funkcija ?? '' })),
              ...Object.entries(FUNKCIJE_SKRBNISTVA).map(([value, label]) => ({ value, label })),
            ]}
            value={funkcija}
            onChange={filter(setFunkcija)}
          />
          <Select
            label="Stanje"
            placeholder="Vse"
            clearable
            data={[
              { value: 'uspelo', label: 'Uspelo' },
              { value: 'napaka', label: 'Napaka' },
            ]}
            value={stanje}
            onChange={filter(setStanje)}
          />
        </SimpleGrid>
        {!klici.data ? (
          <StanjeNalaganja
            napaka={klici.error}
            ponovi={() => void klici.refetch()}
            naslov="Klicev ni bilo mogoče naložiti"
          />
        ) : klici.data.vsebina?.length ? (
          <>
            <Table.ScrollContainer minWidth={1040}>
              <Table highlightOnHover verticalSpacing={6}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Čas</Table.Th>
                    <Table.Th>Instanca</Table.Th>
                    <Table.Th>Uporabnik</Table.Th>
                    <Table.Th>Funkcija</Table.Th>
                    <Table.Th>Model</Table.Th>
                    <Table.Th ta="right">Žetoni (vhod + izhod)</Table.Th>
                    <Table.Th ta="right">Trajanje</Table.Th>
                    <Table.Th ta="right">Strošek (USD)</Table.Th>
                    <Table.Th ta="right">Zaračunano</Table.Th>
                    <Table.Th>Stanje</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {klici.data.vsebina.map((k) => (
                    <Table.Tr
                      key={k.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setIzbran(k.id ?? null)}
                      aria-label={`Klic ${k.nazivFunkcije ?? k.funkcija} ${cas(k.cas)}`}
                    >
                      <Table.Td className="brez-preloma">{cas(k.cas)}</Table.Td>
                      <Table.Td>
                        <InstancaKlica k={k} />
                      </Table.Td>
                      <Table.Td>{k.uporabnik}</Table.Td>
                      <Table.Td>{k.nazivFunkcije ?? k.funkcija}</Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {k.model}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right" className="brez-preloma">
                        {stevilo(k.vhodniZetoni ?? 0)} + {stevilo(k.izhodniZetoni ?? 0)}
                      </Table.Td>
                      <Table.Td ta="right" className="brez-preloma">
                        {trajanje(k.trajanjeMs)}
                      </Table.Td>
                      <Table.Td ta="right">{k.strosek != null ? tocke(k.strosek) : '–'}</Table.Td>
                      <Table.Td ta="right">{k.zaracunano != null ? tocke(k.zaracunano) : ''}</Table.Td>
                      <Table.Td>
                        <StanjeKlica k={k} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {stevilo(stran * VELIKOST + 1)}–{stevilo(Math.min(skupaj, (stran + 1) * VELIKOST))} od {stevilo(skupaj)}
              </Text>
              {strani > 1 && (
                <Pagination total={strani} value={stran + 1} onChange={(v) => setStran(v - 1)} size="sm" />
              )}
            </Group>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            {filtrirano ? 'Ni klicev, ki bi ustrezali filtrom.' : 'Klicev še ni bilo.'}
          </Text>
        )}
      </Stack>
      <PodrobnostiKlica id={izbran} zapri={() => setIzbran(null)} />
    </Kartica>
  );
}

type Klic = Shema['AiKlic'];

function InstancaKlica({ k }: { k: Klic }) {
  return k.companyId ? (
    <Anchor component={Link} to={`/skrbnistvo/${k.companyId}`} size="sm" onClick={(e) => e.stopPropagation()}>
      {k.naziv ?? `#${k.companyId}`}
    </Anchor>
  ) : (
    <Text size="sm" c="dimmed">
      skrbništvo
    </Text>
  );
}

function StanjeKlica({ k }: { k: Klic }) {
  return k.uspelo ? (
    <Badge color="green" variant="light">
      uspelo
    </Badge>
  ) : (
    <Tooltip label={k.napaka} multiline maw={360} disabled={!k.napaka}>
      <Badge color="red" variant="light">
        napaka
      </Badge>
    </Tooltip>
  );
}

/** Podrobnosti klica: podatki iz evidence ter zahteva in odgovor ponudnika (JSON brez slik). */
function PodrobnostiKlica({ id, zapri }: { id: number | null; zapri: () => void }) {
  const podrobnosti = useQuery({
    queryKey: [...KLJUC_AI, 'klic', id],
    enabled: id !== null,
    queryFn: ({ signal }) =>
      klic(() =>
        podatki(api.GET('/api/skrbnik/ai/klici/{id}', { signal, headers: glave(), params: { path: { id: id! } } })),
      ),
  });
  const k = podrobnosti.data;
  return (
    <Modal
      opened={id !== null}
      onClose={zapri}
      title={k ? `${k.nazivFunkcije ?? k.funkcija} · ${cas(k.cas)}` : 'Klic'}
      size="xl"
      radius="lg"
    >
      {!k ? (
        <StanjeNalaganja
          napaka={podrobnosti.error}
          ponovi={() => void podrobnosti.refetch()}
          naslov="Klica ni bilo mogoče naložiti"
        />
      ) : (
        <Stack gap="sm">
          <Podatki
            vrstice={[
              ['Instanca', <InstancaKlica key="i" k={k} />],
              ['Uporabnik', k.uporabnik],
              ['Model', k.model],
              ['Žetoni (vhod + izhod)', `${stevilo(k.vhodniZetoni ?? 0)} + ${stevilo(k.izhodniZetoni ?? 0)}`],
              ['Trajanje', trajanje(k.trajanjeMs)],
              ['Strošek (USD)', k.strosek != null ? tocke(k.strosek) : '–'],
              ['Zaračunano', k.zaracunano != null ? `${tocke(k.zaracunano)} točk` : '–'],
              ['Stanje', <StanjeKlica key="s" k={k} />],
            ]}
          />
          {!k.uspelo && k.napaka && (
            <Alert color="red" variant="light">
              {k.napaka}
            </Alert>
          )}
          {k.vsebina ? (
            <>
              <Text size="xs" c="dimmed">
                Zahteva in odgovor, kot sta šla k ponudniku in nazaj. Slike, dokumenti in drugi binarni podatki niso
                shranjeni – namesto njih je opis (vrsta in velikost).
              </Text>
              <Tabs defaultValue="zahteva" keepMounted={false}>
                <Tabs.List>
                  <Tabs.Tab value="zahteva">Zahteva</Tabs.Tab>
                  <Tabs.Tab value="odgovor">Odgovor</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="zahteva" pt="sm">
                  <VsebinaKlica besedilo={k.zahteva} prazno="Zahteva ni shranjena." />
                </Tabs.Panel>
                <Tabs.Panel value="odgovor" pt="sm">
                  <VsebinaKlica besedilo={k.odgovor} prazno="Ponudnik ni odgovoril (npr. časovna omejitev)." />
                </Tabs.Panel>
              </Tabs>
            </>
          ) : (
            <Text size="sm" c="dimmed">
              Vsebina klica ni shranjena: klic je starejši od 90 dni ali iz časa pred evidenco vsebine.
            </Text>
          )}
        </Stack>
      )}
    </Modal>
  );
}

/** JSON lepo zamaknjen (skrajšan ali drug odgovor ostane, kot je) z gumbom za kopiranje. */
function VsebinaKlica({ besedilo, prazno }: { besedilo?: string | null; prazno: string }) {
  if (!besedilo) {
    return (
      <Text size="sm" c="dimmed">
        {prazno}
      </Text>
    );
  }
  let lepo = besedilo;
  try {
    lepo = JSON.stringify(JSON.parse(besedilo), null, 2);
  } catch {
    // ni JSON (skrajšano ali stran napake), pokaži kot je
  }
  return (
    <Stack gap="xs">
      <Group justify="flex-end">
        <CopyButton value={lepo}>
          {({ copied, copy }) => (
            <Button
              size="xs"
              variant="light"
              onClick={copy}
              leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            >
              {copied ? 'Kopirano' : 'Kopiraj'}
            </Button>
          )}
        </CopyButton>
      </Group>
      <Code block style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 520, overflowY: 'auto' }}>
        {lepo}
      </Code>
    </Stack>
  );
}
